import { QueryClient, useQuery } from 'react-query';

import { GqlRequestOptions } from '@common/api-request';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { checkValidCatalogProductId, useCatalogProductIdParams } from '@domains/pdp-common';

import { useOptionContext } from '../context/OptionProvider';
import { getCatalogProductDetailPageOption } from '../graphql/select-option.2';
import {
  GetCatalogProductDetailPageOption_pdp_option_info,
  PdpCatalogFulfillmentType,
  PdpCouponAvailableStatus,
  PdpCouponDiscountType,
} from '../graphql/select-option_types.2';

const ERROR_INVALID_PARAMS_CATALOG_PRODUCT_ID = new Error('invalid params { catalog_product_id }');
const ERROR_FAILED_FETCH = new Error('failed to fetch');

interface OptionsParams {
  catalog_product_id: string;
  entry_source_type?: string;
}

const queryOptions = {
  // MEMO: 재입고 알림 실시간성 보장을 위해 옵션 추가
  refetchOnWindowFocus: false,
  cacheTime: Infinity,
  staleTime: Infinity,
};

export async function requestOptions(params: OptionsParams, ctx?: GqlRequestOptions) {
  try {
    if (!checkValidCatalogProductId(params.catalog_product_id)) {
      throw ERROR_INVALID_PARAMS_CATALOG_PRODUCT_ID;
    }

    const {
      data: { pdp_option_info },
    } = await getCatalogProductDetailPageOption(
      {
        catalog_product_id: params.catalog_product_id,
        input: params,
      },
      { ...ctx, show_alert: false },
    );
    if (!pdp_option_info) {
      throw ERROR_FAILED_FETCH;
    }
    return pdp_option_info;
  } catch (e) {
    console.error(e);
  }
}

export const OPTIONS_QUERY_KEY = 'getCatalogProductOptionsInfo';

export function getOptionsOption(params: OptionsParams, options?: GqlRequestOptions) {
  return {
    queryKey: [OPTIONS_QUERY_KEY],
    queryFn: async function () {
      return await requestOptions(params, options);
    },
    ...queryOptions,
    enabled: checkValidCatalogProductId(params.catalog_product_id),
  };
}

export async function prefetchOptions(params: OptionsParams, options: GqlRequestOptions, query_client: QueryClient) {
  const query_options = getOptionsOption(params, options);
  await query_client.prefetchQuery(query_options);

  return await query_client.getQueryData(query_options.queryKey);
}

export const useOptions = (productId?: string) => {
  const { entry_source_type } = useOptionContext();
  const { catalog_product_id } = useCatalogProductIdParams();

  const DEFAULT_DATA: GetCatalogProductDetailPageOption_pdp_option_info = {
    catalog_product: {
      shop_id: 0,
      shop_name: '',
      shop_main_domain: '',
      id: '',
      name: '',
      scheduled_sale_date: null,
      minimum_order_quantity: 0,
      maximum_order_quantity: null,
      external_code: null,
      fulfillment_type: PdpCatalogFulfillmentType.MERCHANT,
      matching_catalog_product_info: null,
      meta_catalog_product_info: null,
      product_image_list: [],
      product_option_list: [],
      matched_item_list: [],
      zigzin_item_list: [],
      is_only_zigzin_button_visible: false,
      custom_input_option_list: [],
      color_image_list: [],
      product_additional_option_list: null,
      additional_item_list: null,
      promotion_info: null,
      coupon_available_status: PdpCouponAvailableStatus.UNKNOWN,
      store_deal_banner: null,
      discount_info: null,
      managed_category_list: null,
      estimated_shipping_date: null,
      shipping_fee: null,
      shipping_company: null,
      product_price: {
        first_order_discount: {
          price: 0,
          promotion_id: '',
          discount_type: PdpCouponDiscountType.AMOUNT,
          discount_amount: 0,
          discount_rate_bp: 0,
          min_required_amount: 0,
        },
        coupon_discount_info_list: [],
        display_final_price: {
          final_price: {
            badge: {
              text: '',
              color: {
                normal: '',
              },
            },
            color: {
              normal: '',
            },
          },
          final_price_additional: null,
        },
        product_promotion_discount_info: {
          discount_amount: 0,
        },
        max_price_info: {
          price: 0,
        },
        final_discount_info: {
          discount_price: 0,
        },
      },
      trait_list: null,
      category_list: null,
      minimum_order_quantity_type: null,
    },
    flags: {
      is_purchase_only_one_at_time: false,
      is_cart_button_visible: false,
    },
    key_color: {
      buy_button: {
        text: {
          disabled: '',
          enabled: '',
        },
        background: {
          disabled: '',
          enabled: '',
        },
      },
      discount_info_of_atf: '',
    },
  };

  const queryResponse = useQuery(
    getOptionsOption({ catalog_product_id: productId || catalog_product_id, entry_source_type }),
  );

  return {
    ...queryResponse,
    options: queryResponse.error || !queryResponse.data ? DEFAULT_DATA : queryResponse.data,
  };
};
